import { useCallback, useEffect, useState } from 'react';
import {
  AttributionData,
  GlobalData,
  TrackingPayload,
} from '../interfaces/tracking/TrackingPayload';
import {
  generateEventId,
  getAttribution,
  getConsentState,
  getContextData,
  getGAClientId,
  getStapeUserId,
  getUserId,
  getUserProperties,
} from '../utils/tracking/tracking.utils';
import {
  useGetMeQuery,
  useGetTenantQuery,
  useGetUserQuery,
} from '../services/meApi';
import Keycloak from 'keycloak-js';
import { useKeycloak } from '@react-keycloak/web';
import { TrackingEvents } from '../enums';

const ACTIPAY_SENT_EVENTS_STORAGE_KEY = 'actipay_sent_events';
const ACTIPAY_REPEATABLE_EVENTS = new Set<string>([
  TrackingEvents.pageView,
  TrackingEvents.purchaseInApp,
  TrackingEvents.submitKyc,
  TrackingEvents.selectItem,
]);
export const ACTIPAY_DEV_HEADER_STORAGE_KEY = 'actipay_dev_tracking_header';

const hasActipayEventAlreadyBeenSent = (eventName: string) => {
  if (ACTIPAY_REPEATABLE_EVENTS.has(eventName)) {
    return false;
  }

  try {
    const sentEvents = JSON.parse(
      sessionStorage.getItem(ACTIPAY_SENT_EVENTS_STORAGE_KEY) || '[]',
    );

    return Array.isArray(sentEvents) && sentEvents.includes(eventName);
  } catch (error) {
    console.error('[Actipay Tracking Storage Error]', error);
    return false;
  }
};

const rememberActipayEvent = (eventName: string) => {
  if (ACTIPAY_REPEATABLE_EVENTS.has(eventName)) {
    return;
  }

  try {
    const sentEvents = JSON.parse(
      sessionStorage.getItem(ACTIPAY_SENT_EVENTS_STORAGE_KEY) || '[]',
    );
    const updatedSentEvents = Array.isArray(sentEvents)
      ? [...new Set([...sentEvents, eventName])]
      : [eventName];

    sessionStorage.setItem(
      ACTIPAY_SENT_EVENTS_STORAGE_KEY,
      JSON.stringify(updatedSentEvents),
    );
  } catch (error) {
    console.error('[Actipay Tracking Storage Error]', error);
  }
};

export const canSetHeader = () => {
  return [
    'app-dev.actipay.fr',
    'app-dev.actipay.local',
    'localhost:3000',
  ].includes(window.location.host);
};

const getDevHeader = () => {
  if (!canSetHeader()) {
    return null;
  }

  try {
    const storedHeader = JSON.parse(
      localStorage.getItem(ACTIPAY_DEV_HEADER_STORAGE_KEY) || 'null',
    );

    if (
      storedHeader &&
      typeof storedHeader.name === 'string' &&
      storedHeader.name.trim() &&
      typeof storedHeader.value === 'string' &&
      storedHeader.value.trim()
    ) {
      return {
        name: storedHeader.name.trim(),
        value: storedHeader.value.trim(),
      };
    }
  } catch (error) {
    console.error('[Actipay Tracking Header Error]', error);
  }

  return null;
};

const postMessage = (url: string, payload: any, demo?: boolean) => {
  const devHeader = getDevHeader();
  const canSendInProduction =
    window.location.host === 'app.actipay.fr' && !demo;
  const canSendInDevelopment = canSetHeader() && devHeader;

  if (canSendInProduction || canSendInDevelopment) {
    return fetch(url, {
      method: 'POST',
      body: JSON.stringify(payload),
      ...(devHeader && {
        headers: { [devHeader.name]: devHeader.value },
      }),
    });
  } else {
    console.log('ACTIPAY TRACK EVENT');
    console.log(payload);
  }
};

export const useActipayTracking = (kc?: Keycloak) => {
  const { keycloak, initialized } = useKeycloak();
  const keycloakInstance = kc || keycloak;

  const isAuth = initialized && (keycloakInstance?.authenticated ?? false);

  const [attribution, setAttribution] = useState<AttributionData | null>(null);

  const { data: me, isLoading: isLoadingMe } = useGetMeQuery(undefined, {
    skip: !isAuth,
  });
  const { data: tenant, isLoading: isLoadingTenant } = useGetTenantQuery(
    undefined,
    {
      skip: !isAuth,
    },
  );
  const { data: userInfo, isLoading: isLoadingUser } = useGetUserQuery(
    { userId: me?.uuid || '' },
    { skip: !isAuth || !me },
  );

  const isProfileLoading =
    isAuth &&
    (isLoadingMe || isLoadingTenant || isLoadingUser || !me || !userInfo);

  useEffect(() => {
    const data = getAttribution();
    setAttribution(data);
  }, []);

  const getActipayGlobalData = useCallback((): GlobalData => {
    const rawUserData = userInfo
      ? {
          email: userInfo.login,
          phone: userInfo.phone,
          first_name: userInfo.firstname,
          last_name: userInfo.lastname,
          siren: tenant?.mainMasterRef,
        }
      : {};

    const cleanUserData = { ...rawUserData };
    const forbiddenKeys = [
      'kbis',
      'piece_identite',
      'rib',
      'iban',
      'date_naissance',
      'nationalite',
      'adresse_personnelle',
    ];

    forbiddenKeys.forEach((key) => {
      if (key in cleanUserData) {
        console.error(
          `[CRITICAL SECURITY] Blocage d'envoi de la PII interdite : ${key}`,
        );
        delete (cleanUserData as any)[key];
      }
    });

    return {
      user_id: getUserId(),
      client_id: getGAClientId(),
      stape_user_id: getStapeUserId(),
      consent_state: getConsentState(),
      attribution: attribution,
      user_data: cleanUserData,
      user_properties: getUserProperties(),
      context: getContextData(),
    };
  }, [attribution, tenant?.mainMasterRef, userInfo]);

  const actipaySendS2S = useCallback(
    (
      eventName: string,
      customData?: Record<string, any>,
      customEventId?: string,
    ) => {
      const eventId = customEventId || generateEventId();
      const globalData = getActipayGlobalData();

      const payload: TrackingPayload = {
        event_name: eventName,
        event_id: eventId,
        event_time: Math.floor(Date.now() / 1000),
        ...globalData,
        event: customData || {},
      };

      try {
        postMessage('https://rgpd.actipay.fr/data', payload, tenant?.demo);
        return { eventId, payload };
      } catch (error) {
        console.error('[S2S Tracking Error]', error);
        return null;
      }
    },
    [getActipayGlobalData, tenant?.demo],
  );

  const actipayTrackEvent = useCallback(
    (eventName: string, customData?: Record<string, any>) => {
      if (!isAuth || isProfileLoading || typeof window === 'undefined') {
        return false;
      }

      if (hasActipayEventAlreadyBeenSent(eventName)) {
        return false;
      }

      // Mark the event before sending it to prevent duplicates from concurrent calls.
      rememberActipayEvent(eventName);
      const eventId = `evt_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
      const globalData = getActipayGlobalData();

      const dataLayer: any[] = JSON.parse(
        localStorage.getItem('dataLayer') || JSON.stringify([]),
      );
      dataLayer.push({
        event: eventName,
        event_id: eventId,
        monappGlobalData: globalData,
        ...customData,
      });
      localStorage.setItem('dataLayer', JSON.stringify(dataLayer));
      actipaySendS2S(eventName, customData, eventId);
      return true;
    },
    [isAuth, isProfileLoading, getActipayGlobalData, actipaySendS2S],
  );

  return {
    getActipayGlobalData,
    actipaySendS2S,
    actipayTrackEvent,
    isProfileLoading,
  };
};
